/* Admin Center — Project Scopes tab (extracted from AdminPage.tsx) */

import React, { useState, useEffect } from 'react';
import { Icon } from '../../components/ui/Icon';
import { rem } from '../../lib/rem';
import { Trash2 } from 'lucide-react';
import { ColorPickerPopover } from '../../components/ui/color-picker';
import EmptyState from '../../components/EmptyState';
import { DEFAULT_SCOPE_COLORS } from '../../lib/palette';
import { AdminService } from '../../services/adminService';
import type { ProjectScope } from '../../../shared/types';
import { useTableSync } from '../../hooks/useTableSync';
import type { AdminPageProps } from './types';
import { inputStyle, btnPrimary, btnDanger, RowSkeleton } from './shared';

/* ─── Project Scopes Tab ─── */
function ScopesTab({ toast }: { toast: AdminPageProps['toast'] }) {
  const [scopes, setScopes] = useState<ProjectScope[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [editingColor, setEditingColor] = useState('#6c757d');
  const [addingParent, setAddingParent] = useState(false);
  const [addingChildOf, setAddingChildOf] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#2563eb');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load();
  }, []);
  useTableSync(['project_scopes'], () => load());

  async function load() {
    try {
      setScopes(await AdminService.projectScopes.list());
    } finally {
      setLoading(false);
    }
  }

  const parentScopes = scopes.filter((s) => !s.parentId);
  const childScopes = (parentId: string) => scopes.filter((s) => s.parentId === parentId);

  async function addScope(parentId?: string) {
    if (!newName.trim()) return;
    try {
      await AdminService.projectScopes.save({
        id: '',
        name: newName.trim(),
        parentId: parentId || undefined,
        color: newColor,
        sortOrder: scopes.length,
        createdAt: '',
        updatedAt: '',
      });
      setNewName('');
      setNewColor(DEFAULT_SCOPE_COLORS[scopes.length % DEFAULT_SCOPE_COLORS.length] || '#2563eb');
      setAddingParent(false);
      setAddingChildOf(null);
      await load();
      toast('Scope added');
    } catch (err: any) {
      toast('Save failed: ' + err.message, true);
    }
  }

  async function updateScope(id: string) {
    if (!editingName.trim()) return;
    const scope = scopes.find((s) => s.id === id);
    if (scope) {
      try {
        await AdminService.projectScopes.save({
          ...scope,
          name: editingName.trim(),
          color: editingColor,
        });
        setEditingId(null);
        await load();
        toast('Scope updated');
      } catch (err: any) {
        toast('Update failed: ' + err.message, true);
      }
    }
  }

  async function removeScope(id: string) {
    const scope = scopes.find((s) => s.id === id);
    const children = childScopes(id);
    const msg =
      children.length > 0
        ? `Delete "${scope?.name}" and its ${children.length} sub-scope${children.length !== 1 ? 's' : ''}?`
        : `Delete "${scope?.name}"?`;
    if (!confirm(msg)) return;
    try {
      await AdminService.projectScopes.delete(id);
      if (editingId === id) setEditingId(null);
      await load();
      toast('Scope deleted');
    } catch (err: any) {
      toast('Delete failed: ' + err.message, true);
    }
  }

  function startEdit(scope: ProjectScope) {
    setEditingId(scope.id);
    setEditingName(scope.name);
    setEditingColor(scope.color);
  }

  function renderScopeRow(scope: ProjectScope, indent: boolean) {
    const isEditing = editingId === scope.id;
    return (
      <div
        key={scope.id}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          padding: 'var(--space-2) var(--space-3)',
          paddingLeft: indent ? 40 : 12,
          borderBottom: '1px solid var(--bd)',
        }}
      >
        {isEditing ? (
          <>
            <ColorPickerPopover
              value={editingColor}
              onChange={setEditingColor}
              swatches={DEFAULT_SCOPE_COLORS}
              allowAlpha={false}
              aria-label="Scope color"
            />
            <input
              value={editingName}
              onChange={(e) => setEditingName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && updateScope(scope.id)}
              style={{ ...inputStyle, flex: 1 }}
              autoFocus
            />
            <button
              onClick={() => updateScope(scope.id)}
              style={{ ...btnPrimary, padding: `${rem(5)} ${rem(10)}` }}
            >
              Save
            </button>
            <button onClick={() => setEditingId(null)} style={{ ...btnDanger, color: 'var(--tm)' }}>
              Cancel
            </button>
          </>
        ) : (
          <>
            <span
              style={{
                width: rem(14),
                height: rem(14),
                borderRadius: '50%',
                background: scope.color,
                flexShrink: 0,
              }}
            />
            <span
              style={{ flex: 1, fontSize: 'var(--font-size-base)', fontWeight: indent ? 400 : 600 }}
            >
              {scope.name}
            </span>
            {!indent && (
              <button
                onClick={() => {
                  setAddingChildOf(addingChildOf === scope.id ? null : scope.id);
                  setNewName('');
                  setNewColor(
                    DEFAULT_SCOPE_COLORS[scopes.length % DEFAULT_SCOPE_COLORS.length] || '#059669'
                  );
                }}
                style={{
                  padding: `${rem(3)} var(--space-2)`,
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--bd)',
                  background: 'transparent',
                  color: 'var(--ac)',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                + Sub-Scope
              </button>
            )}
            <button onClick={() => startEdit(scope)} style={{ ...btnDanger, color: 'var(--tm)' }}>
              Edit
            </button>
            <button
              onClick={() => removeScope(scope.id)}
              style={btnDanger}
              title="Delete scope"
              aria-label={`Delete scope ${scope.name}`}
            >
              <Icon icon={Trash2} em={14 / 12} />
            </button>
          </>
        )}
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 'var(--space-4)',
        }}
      >
        <div style={{ fontSize: 'var(--font-size-base)', color: 'var(--tm)' }}>
          Define project scopes (disciplines and sub-disciplines). Scopes can be assigned to
          projects.
        </div>
        <button
          onClick={() => {
            setAddingParent(true);
            setAddingChildOf(null);
            setNewName('');
            setNewColor(
              DEFAULT_SCOPE_COLORS[parentScopes.length % DEFAULT_SCOPE_COLORS.length] || '#2563eb'
            );
          }}
          style={btnPrimary}
        >
          + Add Scope
        </button>
      </div>

      {addingParent && (
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-2)',
            marginBottom: 'var(--space-4)',
            alignItems: 'center',
          }}
        >
          <ColorPickerPopover
            value={newColor}
            onChange={setNewColor}
            swatches={DEFAULT_SCOPE_COLORS}
            allowAlpha={false}
            aria-label="New scope color"
          />
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addScope()}
            placeholder="New scope name (e.g., Mechanical, Electrical)..."
            style={{ ...inputStyle, flex: 1 }}
            autoFocus
          />
          <button
            onClick={() => addScope()}
            disabled={!newName.trim()}
            style={{ ...btnPrimary, opacity: newName.trim() ? 1 : 0.5 }}
          >
            Add
          </button>
          <button
            onClick={() => setAddingParent(false)}
            style={{ ...btnDanger, color: 'var(--tm)', border: '1px solid var(--bd)' }}
          >
            Cancel
          </button>
        </div>
      )}

      <div
        style={{
          background: 'var(--sf)',
          borderRadius: rem(8),
          border: '1px solid var(--bd)',
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <RowSkeleton label="Loading scopes" />
        ) : parentScopes.length === 0 && !addingParent ? (
          <EmptyState
            title="No scopes defined yet"
            subtitle='Add scopes like "Mechanical", "Electrical", "Plumbing", "Technology" to categorize project disciplines.'
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {parentScopes.map((parent) => (
              <React.Fragment key={parent.id}>
                {renderScopeRow(parent, false)}
                {childScopes(parent.id).map((child) => renderScopeRow(child, true))}
                {addingChildOf === parent.id && (
                  <div
                    style={{
                      display: 'flex',
                      gap: 'var(--space-2)',
                      padding: 'var(--space-2) var(--space-3)',
                      paddingLeft: rem(40),
                      borderBottom: '1px solid var(--bd)',
                      background: 'rgba(var(--acr),0.03)',
                    }}
                  >
                    <ColorPickerPopover
                      value={newColor}
                      onChange={setNewColor}
                      swatches={DEFAULT_SCOPE_COLORS}
                      allowAlpha={false}
                      aria-label="New sub-scope color"
                    />
                    <input
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && addScope(parent.id)}
                      placeholder={`Sub-scope under ${parent.name}...`}
                      style={{ ...inputStyle, flex: 1 }}
                      autoFocus
                    />
                    <button
                      onClick={() => addScope(parent.id)}
                      disabled={!newName.trim()}
                      style={{
                        ...btnPrimary,
                        padding: `${rem(5)} ${rem(10)}`,
                        opacity: newName.trim() ? 1 : 0.5,
                      }}
                    >
                      Add
                    </button>
                    <button
                      onClick={() => setAddingChildOf(null)}
                      style={{ ...btnDanger, color: 'var(--tm)' }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export { ScopesTab };
